import handleSupergraphRequest from '../utils/handleSupergraphRequest';
import {
    ActivityDetailEnrichment,
    LearnerAttendance,
    LearnerCountsData,
    RosterEntry,
} from '../interfaces/supergraph';

/** Max pks per batched learner-counts request. */
export const LEARNER_COUNTS_BATCH_SIZE = 50;

/**
 * Max waitlisted registrations counted per activity. Waitlists are small in
 * practice; a batched query can't paginate a per-activity connection, so we
 * request a single page this size and count within it.
 */
const WAITLIST_PAGE_SIZE = 200;

/** Raw learningActivity shape (registered/attended counts) per pk. */
interface RawLearnerCountsActivity {
    pk: string;
    registrationCounter: number;
    attendedCounter: number;
}

/** Raw waitlisted registrations connection per pk (for counting). */
interface RawWaitlistConnection {
    nodes: Array<{ withdrawnOn: string | null }>;
}

/** One batched-query response entry: aliased activity + waitlist fields. */
type RawLearnerCountsResponse = Record<
    string,
    RawLearnerCountsActivity | RawWaitlistConnection | null
>;

/** Split a list of pks into fixed-size batches. */
const batchPks = (pks: string[], batchSize: number): string[][] => {
    const batches: string[][] = [];
    for (let start = 0; start < pks.length; start += batchSize) {
        batches.push(pks.slice(start, start + batchSize));
    }
    return batches;
};

/**
 * Build a single query that fetches learner counts for a batch of pks. Each pk
 * gets two aliased fields bound to its variable ($p0, $p1, …):
 *   - `aN`: registered/attended counts from the LearningActivity entity
 *   - `wN`: WAITLISTED registrations from RMS (the source of truth — the
 *     LearningActivity.waitlisted field is a stale LADS-synced copy), counted
 *     client-side (excluding withdrawn) since RMS exposes no waitlist count.
 *
 *   query GetLearnerCounts($p0: ID!) {
 *     a0: learningActivity(pk: $p0) { pk registrationCounter attendedCounter }
 *     w0: registrationsByLearningActivityId(
 *           learningActivityId: $p0, registrationStatus: WAITLISTED, pageSize: 200
 *         ) { nodes { withdrawnOn } }
 *   }
 */
const buildLearnerCountsQuery = (batchSize: number): string => {
    const variableDeclarations = Array.from(
        { length: batchSize },
        (_, i) => `$p${i}: ID!`,
    ).join(', ');

    const aliasedFields = Array.from({ length: batchSize }, (_, i) =>
        [
            `a${i}: learningActivity(pk: $p${i}) { pk registrationCounter attendedCounter }`,
            `w${i}: registrationsByLearningActivityId(learningActivityId: $p${i}, registrationStatus: WAITLISTED, pageSize: ${WAITLIST_PAGE_SIZE}) { nodes { withdrawnOn } }`,
        ].join('\n            '),
    ).join('\n            ');

    return `
        query GetLearnerCounts(${variableDeclarations}) {
            ${aliasedFields}
        }
    `;
};

/** Map a batch of pks to its `{ p0: pk, p1: pk, … }` GraphQL variables. */
const buildLearnerCountsVariables = (batch: string[]): Record<string, string> =>
    Object.fromEntries(batch.map((pk, i) => [`p${i}`, pk]));

/** Count currently-waitlisted registrations, excluding withdrawn ones. */
const countWaitlisted = (connection: RawWaitlistConnection | null): number =>
    (connection?.nodes ?? []).filter((node) => node.withdrawnOn == null).length;

/**
 * Fetch live learner counts for a page of activities from the Supergraph:
 * RMS registrationCounter, AMS attendedCounter, and the RMS-sourced WAITLISTED
 * count (see {@link buildLearnerCountsQuery}).
 *
 * Deduplicates the pks, splits them into batches of
 * {@link LEARNER_COUNTS_BATCH_SIZE}, and requests all batches concurrently.
 * Pks that don't resolve are simply absent from the result (callers fall back
 * to the REST values).
 *
 * @param pks - Activity primary keys for the current page (pre-filtered to
 *              AMS/RMS-supported lms_types by the caller)
 * @return A promise resolving with a pk → counts map
 */
const getActivityLearnerCounts = async (
    pks: string[],
): Promise<Record<string, LearnerCountsData>> => {
    const uniquePks = Array.from(new Set(pks.filter(Boolean)));
    if (uniquePks.length === 0) {
        return {};
    }

    const batches = batchPks(uniquePks, LEARNER_COUNTS_BATCH_SIZE);

    // allSettled (not all): a transient failure in one batch must not discard
    // the counts from batches that DID resolve — merge only fulfilled results,
    // consistent with the fail-open intent (missing pks fall back to REST).
    //
    // allowPartial: a per-activity GraphQL error (e.g. NOT_AUTHORIZED on one
    // aliased pk) must not throw away the other ~49 aliases in the same batch.
    // The parsing loop already tolerates missing/null aliases.
    const batchResults = await Promise.allSettled(
        batches.map((batch) =>
            handleSupergraphRequest<RawLearnerCountsResponse>(
                buildLearnerCountsQuery(batch.length),
                buildLearnerCountsVariables(batch),
                { allowPartial: true },
            ),
        ),
    );

    const countsByPk: Record<string, LearnerCountsData> = {};
    for (const batchResult of batchResults) {
        if (batchResult.status !== 'fulfilled') {
            continue;
        }
        // Each pk contributes an `aN` (activity) and a `wN` (waitlist) alias.
        // Iterate the `aN` entries and pair each with its `wN` sibling.
        for (const [alias, value] of Object.entries(batchResult.value)) {
            if (!alias.startsWith('a')) {
                continue;
            }
            const activity = value as RawLearnerCountsActivity | null;
            if (!activity?.pk) {
                continue;
            }
            const waitlist = batchResult.value[
                `w${alias.slice(1)}`
            ] as RawWaitlistConnection | null;
            countsByPk[activity.pk] = {
                registrationCounter: activity.registrationCounter,
                attendedCounter: activity.attendedCounter,
                waitlisted: countWaitlisted(waitlist),
            };
        }
    }
    return countsByPk;
};

// ---------------------------------------------------------------------------
// Activity detail enrichment (single federated query)
// Covers: CSAT (EvaluationService), per-org counts (RMS/AMS).
// Each sub-service resolves independently; a NOT_AUTHORIZED on one field
// surfaces as a GraphQL error entry while other fields still return, so the
// caller receives partial data (fail-open per field).
// ---------------------------------------------------------------------------

export const GET_ACTIVITY_DETAIL_ENRICHMENT = `
    query GetActivityDetailEnrichment($id: ID!) {
        learningActivity(pk: $id) {
            pk
            csat {
                overall_score
                instructor_score
                completed_responses
            }
            registrationCounterPerOrganization {
                registrationCounter
                organization { id name }
            }
            attendedCounterPerOrganization {
                attendedCounter
                organization { id name }
            }
        }
    }
`;

interface RawEnrichmentActivity {
    pk: string;
    csat: {
        overall_score: number;
        instructor_score: number;
        completed_responses: number;
    } | null;
    registrationCounterPerOrganization: Array<{
        registrationCounter: number;
        organization: { id: string; name: string | null } | null;
    }> | null;
    attendedCounterPerOrganization: Array<{
        attendedCounter: number;
        organization: { id: string; name: string | null } | null;
    }> | null;
}

/**
 * Fetch cross-subgraph enrichment for the activity detail page in one
 * federated query. Field-level failures (e.g. a subgraph denying user
 * context) surface as GraphQL errors alongside partial data — this client
 * keeps whatever resolved and nulls the rest, so callers can render each
 * panel independently.
 */
const getActivityDetailEnrichment = async (
    learningActivityId: string,
): Promise<ActivityDetailEnrichment> => {
    const empty: ActivityDetailEnrichment = {
        csat: null,
        registrationsPerOrg: [],
        attendedPerOrg: [],
    };

    // Partial data is expected: run with allowPartial so per-field
    // NOT_AUTHORIZED errors don't discard the fields that DID resolve.
    let detail: { learningActivity: RawEnrichmentActivity | null };
    try {
        detail = await handleSupergraphRequest<{
            learningActivity: RawEnrichmentActivity | null;
        }>(
            GET_ACTIVITY_DETAIL_ENRICHMENT,
            { id: learningActivityId },
            { allowPartial: true },
        );
    } catch {
        return { ...empty };
    }

    const result = { ...empty };

    if (detail?.learningActivity) {
        const a = detail.learningActivity;
        result.csat = a.csat
            ? {
                  overallScore: a.csat.overall_score,
                  instructorScore: a.csat.instructor_score,
                  completedResponses: a.csat.completed_responses,
              }
            : null;
        result.registrationsPerOrg = (
            a.registrationCounterPerOrganization ?? []
        ).map((r) => ({
            count: r.registrationCounter,
            organizationName: r.organization?.name ?? 'Unknown org',
        }));
        result.attendedPerOrg = (a.attendedCounterPerOrganization ?? []).map(
            (r) => ({
                count: r.attendedCounter,
                organizationName: r.organization?.name ?? 'Unknown org',
            }),
        );
    }

    return result;
};

// ---------------------------------------------------------------------------
// Attendance roster: per-learner, per-session attendance records from AMS
// ---------------------------------------------------------------------------

export const GET_CLASS_ATTENDANCE = `
    query GetClassAttendance($id: ID!, $cursor: String, $pageSize: Int) {
        classAttendanceByLearningActivityId(
            learningActivityId: $id, cursor: $cursor, pageSize: $pageSize
        ) {
            nodes {
                learner { id firstName lastName emailAddress }
                learningActivityAttendanceStatus
                deliverySessionAttendanceRecords {
                    attendanceStatus
                    attendanceDurationPercentage
                    amsDeliverySession { id startTime endTime }
                }
            }
            pageInfo { cursor }
        }
    }
`;

interface RawAttendanceNode {
    learner: {
        id: string;
        firstName: string | null;
        lastName: string | null;
        emailAddress: string | null;
    };
    learningActivityAttendanceStatus: string;
    deliverySessionAttendanceRecords: Array<{
        attendanceStatus: string;
        attendanceDurationPercentage: number | null;
        amsDeliverySession: {
            id: string;
            startTime: number | null;
            endTime: number | null;
        } | null;
    }> | null;
}

/**
 * Fetch the per-learner attendance roster for an activity from AMS,
 * following cursor pagination to completion (rosters cap at ~500).
 */
const getClassAttendance = async (
    learningActivityId: string,
): Promise<LearnerAttendance[]> => {
    const learners: LearnerAttendance[] = [];
    let cursor: string | null = null;
    // Hard page cap mirrors the backend's pagination guard.
    for (let page = 0; page < 10; page++) {
        const data = await handleSupergraphRequest<{
            classAttendanceByLearningActivityId: {
                nodes: RawAttendanceNode[];
                pageInfo: { cursor: string | null } | null;
            } | null;
        }>(GET_CLASS_ATTENDANCE, {
            id: learningActivityId,
            cursor,
            pageSize: 100,
        });

        const connection = data.classAttendanceByLearningActivityId;
        if (!connection) break;
        for (const node of connection.nodes) {
            learners.push({
                learnerId: node.learner.id,
                learnerName: [node.learner.firstName, node.learner.lastName]
                    .filter(Boolean)
                    .join(' '),
                learnerEmail: node.learner.emailAddress ?? '',
                rollupStatus: node.learningActivityAttendanceStatus,
                sessions: (node.deliverySessionAttendanceRecords ?? []).map(
                    (r) => ({
                        attendanceStatus: r.attendanceStatus,
                        attendanceDurationPercentage:
                            r.attendanceDurationPercentage,
                        sessionStartTime:
                            r.amsDeliverySession?.startTime ?? null,
                        sessionEndTime: r.amsDeliverySession?.endTime ?? null,
                    }),
                ),
            });
        }
        cursor = connection.pageInfo?.cursor ?? null;
        if (!cursor) break;
    }
    return learners;
};

// ---------------------------------------------------------------------------
// Registration roster: who is registered/waitlisted, from RMS
// ---------------------------------------------------------------------------

export const GET_REGISTRATION_ROSTER = `
    query GetRegistrationRoster($id: ID!, $status: RegistrationStatus, $cursor: String, $pageSize: Int) {
        registrationsByLearningActivityId(
            learningActivityId: $id, registrationStatus: $status,
            cursor: $cursor, pageSize: $pageSize
        ) {
            nodes {
                id
                registrationStatus
                user { id firstName lastName emailAddress }
                organization { id name }
            }
            pageInfo { cursor }
        }
    }
`;

interface RawRegistrationNode {
    id: string;
    registrationStatus: string;
    user: {
        id: string;
        firstName: string | null;
        lastName: string | null;
        emailAddress: string | null;
    };
    organization: { id: string; name: string | null } | null;
}

/**
 * Fetch the registration roster for an activity from RMS, optionally
 * filtered by status (e.g. WAITLISTED), following pagination.
 */
const getRegistrationRoster = async (
    learningActivityId: string,
    registrationStatus?: string,
): Promise<RosterEntry[]> => {
    const entries: RosterEntry[] = [];
    let cursor: string | null = null;
    for (let page = 0; page < 10; page++) {
        const data = await handleSupergraphRequest<{
            registrationsByLearningActivityId: {
                nodes: RawRegistrationNode[];
                pageInfo: { cursor: string | null } | null;
            } | null;
        }>(GET_REGISTRATION_ROSTER, {
            id: learningActivityId,
            status: registrationStatus ?? null,
            cursor,
            pageSize: 100,
        });

        const connection = data.registrationsByLearningActivityId;
        if (!connection) break;
        for (const node of connection.nodes) {
            entries.push({
                registrationId: node.id,
                learnerName: [node.user.firstName, node.user.lastName]
                    .filter(Boolean)
                    .join(' '),
                learnerEmail: node.user.emailAddress ?? '',
                registrationStatus: node.registrationStatus,
                organizationName: node.organization?.name ?? null,
            });
        }
        cursor = connection.pageInfo?.cursor ?? null;
        if (!cursor) break;
    }
    return entries;
};

const supergraphApi = {
    getActivityLearnerCounts,
    getActivityDetailEnrichment,
    getClassAttendance,
    getRegistrationRoster,
};

export default supergraphApi;
